import { useMemo } from 'react';
import { useShelterStore } from '../../../store/shelterStore';
import type { Shelter } from '../../../types/shelter';
import {
  IconUsers, IconBed, IconHome, IconAlertTriangle,
} from '@tabler/icons-react';

interface Props {
  shelter: Shelter;
  onIntake: () => void;
}

export default function ShelterPortalDashboard({ shelter, onIntake }: Props) {
  const survivors = useShelterStore(s => s.survivors);
  const families  = useShelterStore(s => s.families);

  const active = useMemo(
    () => survivors.filter(s => s.shelterId === String(shelter.id) && s.status === 'checked_in'),
    [survivors, shelter.id],
  );

  const cap = shelter.capacity || 1;
  const current = shelter.currentPopulation || active.length;
  const available = Math.max(0, cap - current);
  const pct = Math.min(100, Math.round((current / cap) * 100));
  const familyCount = families.filter(f => String(f.incidentId) === String(shelter.incidentId)).length;

  const barColor = pct >= 95 ? '#B12525' : pct >= 80 ? '#f59e0b' : '#4B3FFF';

  return (
    <div>
      {/* Occupancy */}
      <div className="sp-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <span className="sp-card-title" style={{ marginBottom: 0 }}>Occupancy</span>
          <span style={{ fontSize: 20, fontWeight: 800, color: barColor }}>{pct}%</span>
        </div>
        <div className="sp-occ-bar-track">
          <div className="sp-occ-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#7B7D7F', marginTop: 4 }}>
          <span>{current} checked in</span>
          <span>Capacity: {cap}</span>
        </div>
      </div>

      {/* Stats */}
      <div className="sp-stat-grid">
        <div className="sp-stat">
          <div className="sp-stat-val" style={{ color: '#4B3FFF' }}>{current}</div>
          <div className="sp-stat-lbl">Current</div>
        </div>
        <div className="sp-stat">
          <div className="sp-stat-val" style={{ color: available === 0 ? '#B12525' : '#059669' }}>{available}</div>
          <div className="sp-stat-lbl">Available</div>
        </div>
        <div className="sp-stat">
          <div className="sp-stat-val" style={{ color: '#7c3aed' }}>{familyCount}</div>
          <div className="sp-stat-lbl">Families</div>
        </div>
        <div className="sp-stat">
          <div className="sp-stat-val" style={{ color: '#f59e0b' }}>{cap}</div>
          <div className="sp-stat-lbl">Capacity</div>
        </div>
      </div>

      {/* Shelter info */}
      <div className="sp-card" style={{ fontSize: 14, color: '#475569' }}>
        {shelter.address && <div style={{ marginBottom: 4 }}>📍 {shelter.address}{shelter.city ? `, ${shelter.city}` : ''}</div>}
        {shelter.contactName && <div style={{ marginBottom: 4 }}>👤 {shelter.contactName}{shelter.contactPhone ? ` · ${shelter.contactPhone}` : ''}</div>}
        {shelter.operatingOrg && <div>🏢 {shelter.operatingOrg}</div>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          {shelter.isAda && <span className="sp-badge sp-badge-blue">ADA</span>}
          {shelter.petFriendly && <span className="sp-badge sp-badge-green">Pet Friendly</span>}
          <span className={`sp-badge ${shelter.status === 'open' ? 'sp-badge-green' : shelter.status === 'at_capacity' ? 'sp-badge-red' : 'sp-badge-gray'}`}>
            {shelter.status.replace('_', ' ')}
          </span>
        </div>
      </div>

      {/* Quick action */}
      {available > 0 ? (
        <button className="sp-btn sp-btn-primary" onClick={onIntake}>
          <IconUsers size={18} /> Check In New Arrival
        </button>
      ) : (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, padding: 16, textAlign: 'center', fontSize: 14, color: '#991b1b', fontWeight: 600 }}>
          <IconAlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Shelter is at capacity
        </div>
      )}

      {/* Alerts */}
      {active.filter(s => s.specialNeeds && s.specialNeeds !== '[]').length > 0 && (
        <div style={{ marginTop: 16, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: 16, fontSize: 14, color: '#92400e' }}>
          ⚠️ {active.filter(s => s.specialNeeds && s.specialNeeds !== '[]').length} resident{active.filter(s => s.specialNeeds && s.specialNeeds !== '[]').length !== 1 ? 's' : ''} with special needs
        </div>
      )}
    </div>
  );
}
